import { message } from "antd";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import i18n from "~/i18n";
import {
  EntityListData,
  FormattedTransaction,
  TransactionListFiltersType,
} from "~/types";
import { apiClient, TransactionTableLoaderResult, intentFailureFromApiResponse } from "~/services/apiClient";
import type { EnsureTransactionReceiptResult } from "~/types/paymentReceipt";
import { getAuthWrapper, getCachedAuthAccessInfo, INTENTS, loadCookiesData , getReadyAuthUser } from "~/utils";
import {
  createServerFilteredShouldRevalidate,
  normalizeSearchParams,
  setLoaderQueryData,
  STALE_TIME_TIER_D,
  withEntityCacheInvalidation,
} from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import {
  buildDefaultTransactionSearch,
  hasExplicitStatusFilter,
  parseTransactionListFilters,
  transactionFiltersToLoaderParams,
} from "../utils/parseTransactionListFilters";
import { isAnyFileModalDocsPath } from "~/utils/fileModalUrl";
import {
  buildUnitListMapFromLoaderResult,
  formatTransactionTableRows,
} from "../utils/formatTransactionTableResult";

const EMPTY_ENTITY_LIST: EntityListData = {
  propertyList: [],
  buildingList: [],
  unitList: [],
  serviceRequestList: [],
  amenityList: [],
  propertyMap: {},
  buildingMap: {},
  unitMap: {},
};

export function transactionTableQueryKey(
  ownerID: string,
  ownerType: APITypes.OwnerType,
  search: string,
) {
  return [
    "transaction-table",
    ownerID,
    ownerType,
    normalizeSearchParams(search),
  ] as const;
}

export const transactionTableShouldRevalidate =
  createServerFilteredShouldRevalidate(
    (owner, url) =>
      transactionTableQueryKey(owner.ownerID, owner.ownerType, url.search),
    STALE_TIME_TIER_D,
  );

export type TransactionTablePageLoaderType = {
  formattedTransactions: FormattedTransaction[];
  filters: TransactionListFiltersType;
  tenantList: APITypes.Tenant[];
  propertyList: APITypes.Property[];
  buildingList: APITypes.Building[];
  unitListMap: EntityListData;
  canCreateTransaction: boolean;
  ownerID: string;
  ownerType: APITypes.OwnerType;
  hasMore: boolean;
  nextToken: string | null;
};

export async function fetchTransactionTableData(
  request: Request,
): Promise<TransactionTablePageLoaderType | Response> {
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const requestUrl = new URL(request.url);
  if (
    !isAnyFileModalDocsPath(requestUrl.pathname) &&
    !hasExplicitStatusFilter(requestUrl.searchParams)
  ) {
    const search = buildDefaultTransactionSearch(requestUrl.searchParams);
    return redirect(`${requestUrl.pathname}?${search}`);
  }

  const transactionListFilters = parseTransactionListFilters(
    requestUrl.searchParams,
  );

  const result = await apiClient.loader<TransactionTableLoaderResult>(
    "transaction-table",
    transactionFiltersToLoaderParams(transactionListFilters, {
      ownerID,
      ownerType,
      userID: user.uid,
    }),
  );

  if (!result.success || !result.data) {
    console.error("Failed to load transactions:", result.error);
    message.error(i18n.t("Sorry_something_went_wrong"));
    return {
      formattedTransactions: [],
      filters: transactionListFilters,
      tenantList: [],
      propertyList: [],
      buildingList: [],
      unitListMap: EMPTY_ENTITY_LIST,
      canCreateTransaction: isAdminOrSuperAdmin,
      ownerID,
      ownerType,
      hasMore: false,
      nextToken: null,
    };
  }

  const canCreateTransaction =
    isAdminOrSuperAdmin ||
    result.data.transactionLifecycleAccess?.createAccessStatus ===
      APITypes.ResourceAccessRightStatus.Granted;

  const formattedTransactions = formatTransactionTableRows(
    result.data.transactionList ?? [],
  );

  const propertyList = result.data.propertyList ?? [];
  const buildingList = result.data.buildingList ?? [];

  return {
    formattedTransactions,
    filters: transactionListFilters,
    tenantList: result.data.tenantList ?? [],
    propertyList,
    buildingList,
    unitListMap: buildUnitListMapFromLoaderResult(result.data),
    canCreateTransaction,
    ownerID,
    ownerType,
    hasMore: result.data.hasMore ?? false,
    nextToken: result.data.nextToken ?? null,
  };
}

export const TransactionTablePageLoader = async ({
  request,
}: LoaderFunctionArgs): Promise<TransactionTablePageLoaderType | Response> => {
  const data = await fetchTransactionTableData(request);
  if (data instanceof Response) {
    return data;
  }

  const auth = getCachedAuthAccessInfo();
  if (auth) {
    setLoaderQueryData(
      transactionTableQueryKey(
        auth.ownerID,
        auth.ownerType,
        new URL(request.url).search,
      ),
      data,
      STALE_TIME_TIER_D,
    );
  }

  return data;
};

export const TransactionTablePageAction = async ({
  request,
}: ActionFunctionArgs) => {
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) return redirect("/explore");

  const formData = await request.formData();
  const intent = formData.get("intent") as string;

  const transactionCacheTargets = [{ entity: "transaction" as const }];
  const mutateTransaction = <T,>(fn: () => Promise<T>) =>
    withEntityCacheInvalidation(transactionCacheTargets, fn);

  switch (intent) {
    case INTENTS._edit_transactions_approve: {
      return mutateTransaction(async () => {
        const payload = JSON.parse(formData.get("body") as string);
        const result = await apiClient.sendIntent<{ success: boolean }>(
          INTENTS._edit_transactions_approve,
          payload,
        );
        if (!result.success) return { success: false, error: result.error };
        return result;
      });
    }
    case INTENTS._edit_transactions_deny: {
      return mutateTransaction(async () => {
        const payload = JSON.parse(formData.get("body") as string);
        const result = await apiClient.sendIntent<{ success: boolean }>(
          INTENTS._edit_transactions_deny,
          payload,
        );
        if (!result.success) return { success: false, error: result.error };
        return result;
      });
    }
    case INTENTS._request_payment: {
      return mutateTransaction(async () => {
        const payload = JSON.parse(formData.get("body") as string);
        const result = await apiClient.sendIntent<APITypes.Transaction>(
          INTENTS._request_payment,
          { entityType: APITypes.EntityType.Unit, ...payload },
        );
        if (!result.success) return { success: false, error: result.error };
        return result;
      });
    }
    case INTENTS._bulk_request_rent_payments: {
      return mutateTransaction(async () => {
        const year = Number(formData.get("year"));
        const month = Number(formData.get("month"));
        const propertyIDRaw = formData.get("propertyID");
        const propertyID =
          typeof propertyIDRaw === "string" && propertyIDRaw.trim()
            ? propertyIDRaw.trim()
            : undefined;
        const result = await apiClient.sendIntent<{
          created: APITypes.Transaction[];
          skipped: Array<{
            leaseID: string;
            unitID: string;
            reason: string;
          }>;
          summary: {
            candidateCount: number;
            createdCount: number;
            skippedCount: number;
          };
        }>(INTENTS._bulk_request_rent_payments, {
          year,
          month,
          ...(propertyID ? { propertyID } : {}),
        });
        if (!result.success || result.data == null) {
          const failure = intentFailureFromApiResponse(
            result,
            "Failed to create rent payment requests",
          );
          message.error(failure.error);
          return failure;
        }
        return result.data;
      });
    }
    case INTENTS._bulk_request_stay_payments: {
      return mutateTransaction(async () => {
        const year = Number(formData.get("year"));
        const month = Number(formData.get("month"));
        const propertyIDRaw = formData.get("propertyID");
        const propertyID =
          typeof propertyIDRaw === "string" && propertyIDRaw.trim()
            ? propertyIDRaw.trim()
            : undefined;
        const result = await apiClient.sendIntent<{
          created: APITypes.Transaction[];
          skipped: Array<{
            stayID: string;
            unitID: string;
            reason: string;
          }>;
          summary: {
            candidateCount: number;
            createdCount: number;
            skippedCount: number;
          };
        }>(INTENTS._bulk_request_stay_payments, {
          year,
          month,
          ...(propertyID ? { propertyID } : {}),
        });
        if (!result.success || result.data == null) {
          const failure = intentFailureFromApiResponse(
            result,
            "Failed to create stay payment requests",
          );
          message.error(failure.error);
          return failure;
        }
        return result.data;
      });
    }
    case INTENTS._ensure_transaction_receipt: {
      const transactionID = formData.get("transactionID") as string;
      const language = (formData.get("language") as string | null)?.trim();
      if (!transactionID?.trim()) {
        return { success: false, error: "transactionID is required" };
      }
      const result = await apiClient.sendIntent<EnsureTransactionReceiptResult>(
        INTENTS._ensure_transaction_receipt,
        {
          transactionID: transactionID.trim(),
          ...(language ? { language } : {}),
        },
      );
      if (!result.success || !result.data) {
        return intentFailureFromApiResponse(
          result,
          "Failed to generate receipt",
        );
      }
      return result.data;
    }
  }
  return {};
};
